import { useState, type ReactNode } from "react";
import { View, Text, ScrollView, Pressable } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Check, ChevronLeft, X } from "lucide-react-native";
import { MoonFilterChip, TextActionButton } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  colors,
  spacing,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { FilterChip as LegacyClinicsFilterChip } from "@louise/clinics/components/ClinicsFilters/components/FilterChip";
import PharmacyScreen from "../access/PharmacyScreen";
import type {
  DevFilterChipAuditVariant,
  DevStackParamList,
} from "./DevNavigator";
import { EMBER } from "../../../onboarding/theme";

type DevNav = NativeStackNavigationProp<DevStackParamList>;
type PharmacyProps = Parameters<typeof PharmacyScreen>[0];

const CASES: {
  variant: DevFilterChipAuditVariant;
  title: string;
  description: string;
}[] = [
  {
    variant: "pharmacy",
    title: "Pharmacy Type Chips",
    description:
      "Hidden Access screen; scroll to the directory section to inspect the type filters.",
  },
  {
    variant: "therapies-filter-comparison",
    title: "Therapies Filter Comparison",
    description:
      "Comparison-only view: Type shows the old local chip replica; Goal shows MoonFilterChip.",
  },
  {
    variant: "document-option-chip-grid",
    title: "Document Option Chip Grid",
    description:
      "Marker and unit picker chips from document upload/preview review flows.",
  },
  {
    variant: "beta-result-chip-button",
    title: "Beta Result Yes/No Chips",
    description:
      "Two-option selector chips from the beta-result recording modal.",
  },
  {
    variant: "legacy-clinics-filter-chip",
    title: "Legacy Clinics FilterChip (Potential Dead Code)",
    description:
      "Potentially old clinics filter stack; not product-reachable from the current Moon app.",
  },
];
const DEFAULT_CASE = CASES[0]!;
const FOR_WHOM_COMPARISON_OPTIONS = [
  ["her", "For Her"],
  ["him", "For Him"],
  ["both", "For Both"],
] as const;
const TYPE_COMPARISON_OPTIONS = [
  ["mind-body", "Mind-Body"],
  ["traditional", "Traditional"],
  ["physical", "Physical"],
  ["nutritional", "Nutritional"],
  ["lifestyle", "Lifestyle"],
  ["clinical", "Clinical Add-ons"],
] as const;
const GOAL_COMPARISON_OPTIONS = [
  ["ivf-support", "IVF Support"],
  ["stress-anxiety", "Stress & Anxiety"],
  ["pcos", "PMOS (ex PCOS)"],
  ["endometriosis", "Endometriosis"],
  ["implantation", "Implantation"],
  ["sperm-quality", "Sperm Quality"],
  ["general-wellness", "General Wellness"],
] as const;
const DOCUMENT_MARKER_OPTIONS = [
  "AMH",
  "FSH",
  "LH",
  "E2",
  "P4",
  "HCG",
] as const;
const DOCUMENT_UNIT_OPTIONS = ["ng/mL", "IU/L", "mIU/mL", "pg/mL"] as const;

function Header({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const navigation = useNavigation<DevNav>();
  return (
    <View style={{ gap: 12 }}>
      <Pressable
        onPress={() => navigation.navigate("DevHome")}
        style={{
          alignSelf: "flex-start",
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingVertical: 8,
        }}
      >
        <ChevronLeft size={16} color={colors.textMid} />
        <Text style={{ color: colors.textMid, fontSize: 13 }}>Dev</Text>
      </Pressable>
      <View style={{ gap: 6 }}>
        <Text
          style={{
            color: colors.text,
            fontSize: 28,
            fontWeight: "700",
          }}
        >
          {title}
        </Text>
        <Text style={{ color: colors.textMid, fontSize: 14, lineHeight: 20 }}>
          {description}
        </Text>
      </View>
    </View>
  );
}

function CaseNav({ active }: { active?: DevFilterChipAuditVariant }) {
  const navigation = useNavigation<DevNav>();
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {CASES.map((item) => {
        const selected = active === item.variant;
        return (
          <Pressable
            key={item.variant}
            onPress={() =>
              navigation.navigate("DevFilterChipAudit", {
                variant: item.variant,
              })
            }
            style={{
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 16,
              backgroundColor: selected
                ? "rgba(59,26,8,0.12)"
                : "rgba(255,255,255,0.45)",
              borderWidth: 1,
              borderColor: selected
                ? "rgba(59,26,8,0.18)"
                : "rgba(255,255,255,0.55)",
            }}
          >
            <Text
              style={{
                color: selected ? colors.text : colors.textMid,
                fontSize: 13,
                fontWeight: selected ? "600" : "400",
              }}
            >
              {item.title}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function StandardPreviewShell({
  active,
  children,
}: {
  active?: DevFilterChipAuditVariant;
  children: ReactNode;
}) {
  const item = CASES.find((entry) => entry.variant === active) ?? DEFAULT_CASE;
  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.lg,
          gap: spacing.lg,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Header title={item.title} description={item.description} />
        <CaseNav active={active} />
        {children}
      </ScrollView>
    </GlassScreenLayout>
  );
}

function PharmacyPreview() {
  const navigation = useNavigation<PharmacyProps["navigation"]>();
  const pharmacyNavigation: PharmacyProps["navigation"] = {
    ...navigation,
    navigate: () => undefined,
  };

  return (
    <PharmacyScreen
      navigation={pharmacyNavigation}
      route={{
        key: "dev-pharmacy-preview",
        name: "Pharmacy",
        params: undefined,
      }}
    />
  );
}

function OldTherapiesChip({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 9,
        borderRadius: 20,
        backgroundColor: selected
          ? `${EMBER.colors.warm}18`
          : "rgba(0,0,0,0.04)",
        borderWidth: 1,
        borderColor: selected ? `${EMBER.colors.warm}30` : "rgba(0,0,0,0.06)",
      }}
    >
      {selected ? (
        <Check size={12} color={EMBER.colors.warm} strokeWidth={3} />
      ) : null}
      <Text
        style={{
          fontSize: 13,
          fontWeight: selected ? "600" : "400",
          color: selected ? EMBER.colors.warm : EMBER.colors.textMid,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        fontSize: 13,
        fontWeight: "700",
        color: EMBER.colors.textMid,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        marginBottom: 12,
      }}
    >
      {children}
    </Text>
  );
}

function ChipRow({ children }: { children: ReactNode }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {children}
    </View>
  );
}

function TherapiesFilterComparisonPreview() {
  const [forWhom, setForWhom] = useState("her");
  const [types, setTypes] = useState(["mind-body", "traditional"]);
  const [goals, setGoals] = useState(["ivf-support", "stress-anxiety"]);

  const toggleValue = (
    value: string,
    selected: string[],
    setSelected: (value: string[]) => void,
  ) => {
    setSelected(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  return (
    <StandardPreviewShell active="therapies-filter-comparison">
      <View
        style={{
          borderRadius: 24,
          overflow: "hidden",
          backgroundColor: EMBER.colors.surface,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.06)",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingHorizontal: 20,
            paddingTop: 20,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderBottomColor: "rgba(0,0,0,0.06)",
          }}
        >
          <Text
            style={{
              fontSize: 22,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            Filters
          </Text>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: "rgba(0,0,0,0.05)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={16} color={EMBER.colors.textLight} />
          </View>
        </View>

        <View style={{ padding: 20, gap: 28, minHeight: 520 }}>
          <View>
            <SectionLabel>For Whom</SectionLabel>
            <ChipRow>
              {FOR_WHOM_COMPARISON_OPTIONS.map(([value, label]) => (
                <OldTherapiesChip
                  key={value}
                  label={label}
                  selected={forWhom === value}
                  onPress={() => setForWhom(value)}
                />
              ))}
            </ChipRow>
          </View>

          <View>
            <SectionLabel>Type - old local chip</SectionLabel>
            <ChipRow>
              {TYPE_COMPARISON_OPTIONS.map(([value, label]) => (
                <OldTherapiesChip
                  key={value}
                  label={label}
                  selected={types.includes(value)}
                  onPress={() => toggleValue(value, types, setTypes)}
                />
              ))}
            </ChipRow>
          </View>

          <View>
            <SectionLabel>Goal - MoonFilterChip</SectionLabel>
            <ChipRow>
              {GOAL_COMPARISON_OPTIONS.map(([value, label]) => (
                <MoonFilterChip
                  key={value}
                  label={label}
                  selected={goals.includes(value)}
                  onPress={() => toggleValue(value, goals, setGoals)}
                  accentColor={colors.deepBrown}
                />
              ))}
            </ChipRow>
          </View>
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            paddingHorizontal: 20,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: "rgba(0,0,0,0.06)",
          }}
        >
          <TextActionButton
            label="Clear all"
            onPress={() => {}}
            color={EMBER.colors.textMid}
          />
          <View
            style={{
              flex: 1,
              paddingVertical: 14,
              borderRadius: 24,
              backgroundColor: EMBER.colors.warm,
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 15, fontWeight: "700", color: "#fff" }}>
              Apply filters
            </Text>
          </View>
        </View>
      </View>
    </StandardPreviewShell>
  );
}

function DocumentOptionChip({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 16,
        backgroundColor: selected ? EMBER.colors.warm : "rgba(0,0,0,0.04)",
        borderWidth: 1,
        borderColor: selected ? EMBER.colors.warm : "rgba(0,0,0,0.06)",
      }}
    >
      <Text
        style={{
          fontSize: 12,
          color: selected ? "#fff" : EMBER.colors.textMid,
          fontWeight: selected ? "600" : "400",
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function DocumentOptionChipGridPreview() {
  const [marker, setMarker] = useState("AMH");
  const [unit, setUnit] = useState("ng/mL");
  const [proposedMarker, setProposedMarker] = useState("AMH");
  const [proposedUnit, setProposedUnit] = useState("ng/mL");
  return (
    <StandardPreviewShell active="document-option-chip-grid">
      <View
        style={{
          backgroundColor: EMBER.colors.surface,
          borderRadius: 20,
          borderWidth: 1,
          borderColor: "rgba(0,0,0,0.06)",
          padding: 18,
          gap: 20,
        }}
      >
        <View>
          <SectionLabel>Current marker options - filled selected</SectionLabel>
          <ChipRow>
            {DOCUMENT_MARKER_OPTIONS.map((option) => (
              <DocumentOptionChip
                key={option}
                label={option}
                selected={marker === option}
                onPress={() => setMarker(option)}
              />
            ))}
          </ChipRow>
        </View>
        <View>
          <SectionLabel>Current unit options - filled selected</SectionLabel>
          <ChipRow>
            {DOCUMENT_UNIT_OPTIONS.map((option) => (
              <DocumentOptionChip
                key={option}
                label={option}
                selected={unit === option}
                onPress={() => setUnit(option)}
              />
            ))}
          </ChipRow>
        </View>
        <View
          style={{
            height: 1,
            backgroundColor: "rgba(0,0,0,0.06)",
            marginVertical: 2,
          }}
        />
        <View>
          <SectionLabel>MoonFilterChip marker options</SectionLabel>
          <ChipRow>
            {DOCUMENT_MARKER_OPTIONS.map((option) => (
              <MoonFilterChip
                key={option}
                label={option}
                selected={proposedMarker === option}
                onPress={() => setProposedMarker(option)}
                accentColor={colors.deepBrown}
              />
            ))}
          </ChipRow>
        </View>
        <View>
          <SectionLabel>MoonFilterChip unit options</SectionLabel>
          <ChipRow>
            {DOCUMENT_UNIT_OPTIONS.map((option) => (
              <MoonFilterChip
                key={option}
                label={option}
                selected={proposedUnit === option}
                onPress={() => setProposedUnit(option)}
                accentColor={colors.deepBrown}
              />
            ))}
          </ChipRow>
        </View>
      </View>
    </StandardPreviewShell>
  );
}

function BetaResultChip({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: selected ? `${EMBER.colors.warm}50` : EMBER.colors.border,
        backgroundColor: selected
          ? `${EMBER.colors.warm}18`
          : pressed
            ? "rgba(255,255,255,0.7)"
            : "rgba(255,255,255,0.5)",
      })}
    >
      <Text
        style={{
          fontSize: 13,
          fontWeight: selected ? "600" : "500",
          color: selected ? EMBER.colors.warm : EMBER.colors.textMid,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function BetaResultChipButtonPreview() {
  const [answer, setAnswer] = useState<"yes" | "no" | null>("yes");
  return (
    <StandardPreviewShell active="beta-result-chip-button">
      <View
        style={{
          backgroundColor: "rgba(255,255,255,0.55)",
          borderRadius: 20,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.6)",
          padding: 18,
          gap: 8,
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: "700",
            color: EMBER.colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        >
          Chemical pregnancy?
        </Text>
        <Text
          style={{
            fontSize: 12.5,
            color: EMBER.colors.textFaint,
            lineHeight: 17,
          }}
        >
          Two-option form selector from the beta result modal.
        </Text>
        <View style={{ flexDirection: "row", gap: 8, marginTop: 4 }}>
          <BetaResultChip
            label="Yes"
            selected={answer === "yes"}
            onPress={() => setAnswer(answer === "yes" ? null : "yes")}
          />
          <BetaResultChip
            label="No"
            selected={answer === "no"}
            onPress={() => setAnswer(answer === "no" ? null : "no")}
          />
        </View>
      </View>
    </StandardPreviewShell>
  );
}

function LegacyClinicsFilterChipPreview() {
  const [selected, setSelected] = useState("selected");
  return (
    <StandardPreviewShell active="legacy-clinics-filter-chip">
      <View
        style={{
          backgroundColor: "#0f172a",
          borderRadius: 18,
          padding: 18,
          gap: 12,
        }}
      >
        <Text style={{ color: "#cbd5e1", fontSize: 13 }}>
          Potentially old clinics UI. This chip is still referenced by the old
          packages/clinics filter stack, but I did not find that stack mounted
          by the current Moon app.
        </Text>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <LegacyClinicsFilterChip
            label="Unselected"
            isSelected={selected === "unselected"}
            onPress={() => setSelected("unselected")}
          />
          <LegacyClinicsFilterChip
            label="Selected"
            isSelected={selected === "selected"}
            onPress={() => setSelected("selected")}
          />
          <LegacyClinicsFilterChip
            label="Green dot"
            isSelected={selected === "green-dot"}
            showGreenDot
            onPress={() => setSelected("green-dot")}
          />
        </View>
      </View>
    </StandardPreviewShell>
  );
}

function Overview() {
  return (
    <StandardPreviewShell>
      <View style={{ gap: 10 }}>
        {CASES.map((item) => (
          <Text key={item.variant} style={{ color: colors.textMid }}>
            /dev/filter-chip-audit/{item.variant}
          </Text>
        ))}
      </View>
    </StandardPreviewShell>
  );
}

export default function DevFilterChipAuditScreen() {
  const route = useRoute();
  const params = route.params as
    | { variant?: DevFilterChipAuditVariant }
    | undefined;

  switch (params?.variant) {
    case "pharmacy":
      return <PharmacyPreview />;
    case "therapies-filter-comparison":
      return <TherapiesFilterComparisonPreview />;
    case "document-option-chip-grid":
      return <DocumentOptionChipGridPreview />;
    case "beta-result-chip-button":
      return <BetaResultChipButtonPreview />;
    case "legacy-clinics-filter-chip":
      return <LegacyClinicsFilterChipPreview />;
    default:
      return <Overview />;
  }
}
